import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ChevronLeft, Plus, X, Edit3, Music, ExternalLink, Loader2, ChevronDown, ChevronUp } from "lucide-react";
import api from "../lib/api";
import { Ornament } from "../components/Ornament";
import { UpgradeModal } from "../components/UpgradeModal";

function spotifyLink(title, artist) {
  const q = encodeURIComponent([title, artist].filter(Boolean).join(" "));
  return `https://open.spotify.com/search/${q}`;
}
function youtubeLink(title, artist) {
  const q = encodeURIComponent([title, artist].filter(Boolean).join(" "));
  return `https://www.youtube.com/results?search_query=${q}`;
}

function SongCard({ song, isOwner, onEdit, onDelete }) {
  const [expanded, setExpanded] = useState(false);
  const hasLongMemory = (song.memory || "").length > 0;
  return (
    <div className="p-6 bg-white hairline-cream" style={{ borderRadius: 4 }} data-testid={`song-card-${song.song_id}`}>
      <div className="flex items-start justify-between gap-4">
        <div className="flex-1 min-w-0">
          <p className="text-[#111D33]" style={{ fontFamily: "Cormorant Garamond, serif", fontSize: "1.55rem", fontWeight: 400, lineHeight: 1.3 }} data-testid={`song-title-${song.song_id}`}>
            {song.title}
          </p>
          <p className="label-ui text-[10px] text-[#B8973A] mt-1 tracking-[0.2em]" data-testid={`song-artist-${song.song_id}`}>
            {song.artist || "—"}{song.year ? ` · ${song.year}` : ""}
          </p>
          {song.hook && (
            <p className="text-[#1B2A4A] italic mt-3" style={{ fontFamily: "Cormorant Garamond, serif", fontSize: "1.15rem", lineHeight: 1.55 }} data-testid={`song-hook-${song.song_id}`}>
              &ldquo;{song.hook}&rdquo;
            </p>
          )}
          {hasLongMemory && expanded && (
            <p className="text-[#1B2A4A] mt-4 whitespace-pre-line" style={{ fontFamily: "Georgia, serif", fontSize: "1rem", lineHeight: 1.75 }} data-testid={`song-memory-${song.song_id}`}>
              {song.memory}
            </p>
          )}
          <div className="flex items-center gap-4 mt-4 flex-wrap">
            <a href={spotifyLink(song.title, song.artist)} target="_blank" rel="noopener noreferrer" className="label-ui text-[10px] text-[#8C8278] hover:text-[#B8973A] transition-colors inline-flex items-center gap-1" data-testid={`song-spotify-${song.song_id}`}>
              <ExternalLink size={11} /> LISTEN ON SPOTIFY
            </a>
            <a href={youtubeLink(song.title, song.artist)} target="_blank" rel="noopener noreferrer" className="label-ui text-[10px] text-[#8C8278] hover:text-[#B8973A] transition-colors inline-flex items-center gap-1" data-testid={`song-youtube-${song.song_id}`}>
              <ExternalLink size={11} /> WATCH ON YOUTUBE
            </a>
            {hasLongMemory && (
              <button onClick={() => setExpanded(!expanded)} className="label-ui text-[10px] text-[#B8973A] hover:text-[#111D33] transition-colors inline-flex items-center gap-1 ml-auto" data-testid={`song-expand-${song.song_id}`}>
                {expanded ? <><ChevronUp size={11}/> LESS</> : <><ChevronDown size={11}/> READ THE STORY</>}
              </button>
            )}
          </div>
        </div>
        {isOwner && (
          <div className="flex flex-col gap-2 shrink-0">
            <button onClick={onEdit} className="text-[#8C8278] hover:text-[#111D33] transition-colors" aria-label="Edit" data-testid={`song-edit-${song.song_id}`}>
              <Edit3 size={15} />
            </button>
            <button onClick={onDelete} className="text-[#8C8278] hover:text-red-700 transition-colors" aria-label="Delete" data-testid={`song-delete-${song.song_id}`}>
              <X size={15} />
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

function SongForm({ initial, onSave, onCancel, busy }) {
  const [f, setF] = useState({
    title: initial?.title || "",
    artist: initial?.artist || "",
    year: initial?.year || "",
    hook: initial?.hook || "",
    memory: initial?.memory || "",
  });
  const submit = (e) => {
    e.preventDefault();
    if (!f.title.trim()) return;
    onSave({
      title: f.title.trim(),
      artist: f.artist.trim() || null,
      year: f.year ? parseInt(f.year, 10) || null : null,
      hook: f.hook.trim() || null,
      memory: f.memory.trim() || null,
    });
  };
  return (
    <form onSubmit={submit} className="p-6 bg-[#FAF7F2] hairline-cream" style={{ borderRadius: 4 }} data-testid="song-form">
      <p className="label-ui text-[10px] text-[#B8973A] mb-4 tracking-[0.25em]">{initial ? "EDIT SONG" : "ADD A SONG"}</p>
      <div className="grid grid-cols-1 sm:grid-cols-6 gap-3 mb-3">
        <input required value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} placeholder="Song title" className="sm:col-span-3 px-4 py-3 hairline-cream bg-white" style={{ borderRadius: 4, fontFamily: "Georgia, serif", fontSize: "1rem" }} data-testid="song-title-input" />
        <input value={f.artist} onChange={(e) => setF({ ...f, artist: e.target.value })} placeholder="Artist" className="sm:col-span-2 px-4 py-3 hairline-cream bg-white" style={{ borderRadius: 4, fontFamily: "Georgia, serif", fontSize: "1rem" }} data-testid="song-artist-input" />
        <input value={f.year} onChange={(e) => setF({ ...f, year: e.target.value })} placeholder="Year" type="number" className="sm:col-span-1 px-4 py-3 hairline-cream bg-white" style={{ borderRadius: 4, fontFamily: "Georgia, serif", fontSize: "1rem" }} data-testid="song-year-input" />
      </div>
      <input value={f.hook} onChange={(e) => setF({ ...f, hook: e.target.value })} placeholder="One-line hook — e.g. &lsquo;This played in my mother&rsquo;s kitchen every Sunday&rsquo;" maxLength={200} className="w-full px-4 py-3 hairline-cream bg-white mb-3" style={{ borderRadius: 4, fontFamily: "Cormorant Garamond, serif", fontStyle: "italic", fontSize: "1.05rem" }} data-testid="song-hook-input" />
      <textarea value={f.memory} onChange={(e) => setF({ ...f, memory: e.target.value })} placeholder="The longer memory — take your time. Where you were, who you were with, what the song still does to you." rows={5} maxLength={4000} className="w-full px-4 py-3 hairline-cream bg-white" style={{ borderRadius: 4, fontFamily: "Georgia, serif", fontSize: "1rem", lineHeight: 1.6 }} data-testid="song-memory-input" />
      <div className="flex gap-3 mt-4">
        <button type="button" onClick={onCancel} disabled={busy} className="btn-outline-navy flex-1" data-testid="song-cancel-btn">Cancel</button>
        <button type="submit" disabled={busy || !f.title.trim()} className="btn-primary flex-1 flex items-center justify-center gap-2" data-testid="song-save-btn">
          {busy ? <Loader2 size={14} className="animate-spin" /> : null}
          {initial ? "Save changes" : "Add song"}
        </button>
      </div>
    </form>
  );
}

export default function Soundtrack() {
  const { profileId } = useParams();
  const navigate = useNavigate();
  const [profile, setProfile] = useState(null);
  const [me, setMe] = useState(null);
  const [songs, setSongs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(false);
  const [editing, setEditing] = useState(null);
  const [busy, setBusy] = useState(false);
  const [upgradeOpen, setUpgradeOpen] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const [plist, slist, meRes] = await Promise.all([
          api.get("/profiles"),
          api.get(`/soundtracks/${profileId}`),
          api.get("/auth/me"),
        ]);
        const p = (plist.data || []).find((p) => p.profile_id === profileId);
        if (!p) { navigate("/dashboard"); return; }
        setProfile(p);
        setSongs(slist.data || []);
        setMe(meRes.data);
      } catch (e) { navigate("/dashboard"); } finally { setLoading(false); }
    })();
  }, [profileId, navigate]);

  const tier = me?.subscription_tier || "free";
  const isOwner = !!me && (me.is_family_owner !== false); // best-effort — soundtrack API enforces server-side
  const canEdit = tier !== "free";

  const startAdd = () => {
    if (!canEdit) { setUpgradeOpen(true); return; }
    setEditing(null);
    setAdding(true);
  };

  const saveSong = async (data) => {
    setBusy(true);
    try {
      if (editing) {
        const res = await api.patch(`/soundtracks/${editing.song_id}`, data);
        setSongs((prev) => prev.map((s) => s.song_id === editing.song_id ? res.data : s));
      } else {
        const res = await api.post("/soundtracks", { ...data, profile_id: profileId });
        setSongs((prev) => [...prev, res.data]);
      }
      setAdding(false); setEditing(null);
    } catch (e) {
      if (e?.response?.status === 402) setUpgradeOpen(true);
    } finally { setBusy(false); }
  };

  const removeSong = async (song) => {
    if (!window.confirm(`Remove "${song.title}" from the soundtrack?`)) return;
    try {
      await api.delete(`/soundtracks/${song.song_id}`);
      setSongs((prev) => prev.filter((s) => s.song_id !== song.song_id));
    } catch (e) { /* ignore */ }
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center paper"><p className="italic text-[#8C8278]" style={{ fontFamily: "Cormorant Garamond, serif", fontSize: "1.2rem" }}>Cueing the record player…</p></div>;
  if (!profile) return null;

  return (
    <div className="min-h-screen paper" data-testid="soundtrack-page">
      <header className="max-w-4xl mx-auto px-6 sm:px-10 py-8 flex items-center justify-between">
        <Link to={`/app/profile/${profileId}`} className="flex items-center gap-2 label-ui text-[11px] text-[#8C8278] hover:text-[#111D33] transition-colors" data-testid="soundtrack-back-link">
          <ChevronLeft size={16} /> Back to {profile.name}
        </Link>
        <span style={{ fontFamily: "Cormorant Garamond, serif", letterSpacing: "0.06em" }}>The Living Book</span>
      </header>

      <main className="max-w-3xl mx-auto px-6 sm:px-10 pb-24">
        <div className="text-center mb-12">
          <p className="label-ui text-[11px] text-[#B8973A] mb-3 tracking-[0.3em]" data-testid="soundtrack-eyebrow">
            THE SOUNDTRACK OF {profile.name.toUpperCase()}&apos;S LIFE
          </p>
          <h1 className="text-4xl sm:text-5xl mb-4" style={{ fontFamily: "Cormorant Garamond, serif", fontWeight: 300, lineHeight: 1.15, color: "#111D33" }} data-testid="soundtrack-headline">
            The songs that <span style={{ fontStyle: "italic" }}>shaped you.</span>
          </h1>
          <Ornament className="my-6" />
          <p className="text-[#8C8278] italic max-w-xl mx-auto" style={{ fontFamily: "Cormorant Garamond, serif", fontSize: "1.15rem", lineHeight: 1.6 }}>
            Every life has a record collection. Add the songs that still stop you where you stand,
            and the memory that plays underneath them.
          </p>
        </div>

        {songs.length === 0 && !adding && (
          <div className="text-center py-10">
            <Music size={28} className="text-[#B8973A] mx-auto mb-4" />
            <p className="italic text-[#8C8278] mb-6" style={{ fontFamily: "Cormorant Garamond, serif", fontSize: "1.15rem" }}>
              No songs yet. Add the first one.
            </p>
            <button onClick={startAdd} className="btn-gold" data-testid="soundtrack-add-first-btn">
              <Plus size={14} className="inline mr-1" /> Add your first song
            </button>
          </div>
        )}

        {songs.length > 0 && (
          <div className="space-y-4" data-testid="soundtrack-list">
            {songs.map((s) => (
              <SongCard
                key={s.song_id}
                song={s}
                isOwner={isOwner && canEdit}
                onEdit={() => { setEditing(s); setAdding(false); }}
                onDelete={() => removeSong(s)}
              />
            ))}
          </div>
        )}

        {(adding || editing) && (
          <div className="mt-6" data-testid="song-form-wrap">
            <SongForm
              initial={editing}
              onSave={saveSong}
              onCancel={() => { setAdding(false); setEditing(null); }}
              busy={busy}
            />
          </div>
        )}

        {!adding && !editing && songs.length > 0 && (
          <div className="text-center mt-8">
            <button onClick={startAdd} className="btn-outline-navy inline-flex items-center gap-2" data-testid="soundtrack-add-more-btn">
              <Plus size={14} /> Add another song
            </button>
          </div>
        )}

        <p className="text-center italic text-[#B8973A] mt-16" style={{ fontFamily: "Cormorant Garamond, serif", fontSize: "1.05rem" }} data-testid="soundtrack-manifesto">
          &quot;Every life has a soundtrack. Yours belongs beside your words.&quot;
        </p>
      </main>

      <UpgradeModal open={upgradeOpen} onClose={() => setUpgradeOpen(false)} reason="default" />
    </div>
  );
}
